import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import CheckboxTree from 'react-checkbox-tree';
import { Input, Checkbox } from 'syngenta-digital-cropwise-react-ui-kit';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import './styles.css';
import { isEqual } from 'lodash';
import {
  ArrowRight,
  ArrowDropDown,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox
} from '@material-ui/icons';
import { filterCropSeason } from 'screens/CropSeasons/helpers/dataHelpers';
import {
  selectedCropzones,
  traverseFarms,
  traverseFields
} from 'screens/CropSeasons/CropSeason/helpers/propertyHelpers';

const groupByFilterPropertyDefaultPropOnChange = () => {};
const groupByFilterPropertyDefaultPropValue = [];
const groupByFilterPropertyDefaultPropCropZonesChecked = [];
const groupByFilterPropertyDefaultPropSetCropZonesChecked = () => {};
const GroupByFilterProperty = ({
  data,
  value = groupByFilterPropertyDefaultPropValue,
  enableFilter = false,
  onChange = groupByFilterPropertyDefaultPropOnChange,
  disabled = false,
  cropZonesChecked = groupByFilterPropertyDefaultPropCropZonesChecked,
  setCropZonesChecked = groupByFilterPropertyDefaultPropSetCropZonesChecked,
  onSelectAll = undefined
}) => {
  const [expanded, setExpanded] = useState([]);
  const [filterText, setFilterText] = useState();
  const [nodesFiltered, setNodesFiltered] = useState([]);

  const checked = useMemo(() => cropZonesChecked || value, [
    cropZonesChecked,
    value
  ]);

  const fft = useMemo(
    () =>
      data?.map(farm => ({
        value: farm.id,
        className: farm.archived ? 'opacity-50' : '',
        label: farm.name + (farm.archived ? ' (archived)' : ''),
        type: 'farm',
        disabled: traverseFarms(farm.selectedCropSeason, farm.fields) === 0,
        ...(farm.fields.length > 0
          ? {
              children: farm.fields?.map(field => ({
                className: field.archived && !farm.archived ? 'opacity-50' : '',
                value: field.id,
                label: field.name + (field.archived ? ' (archived)' : ''),
                type: 'field',
                disabled:
                  traverseFields(farm.selectedCropSeason, field.cropzones) ===
                  0,
                ...(field.cropzones.length > 0
                  ? {
                      children: field.cropzones?.map(cropzone => ({
                        value: cropzone.id,
                        className:
                          cropzone.archived && !field.archived
                            ? 'opacity-50'
                            : '',
                        label:
                          cropzone.name +
                          (cropzone.archived ? ' (archived)' : ''),
                        farmId: farm.id,
                        type: 'cropzone',
                        disabled:
                          filterCropSeason(
                            farm.selectedCropSeason,
                            cropzone.cropSeasons
                          ) === 0
                      }))
                    }
                  : {})
              }))
            }
          : {})
      })) || [],
    [data]
  );

  const allCropzoneIds = useMemo(() => selectedCropzones(fft, []), [fft]);
  const allCropzoneIdSet = useMemo(() => new Set(allCropzoneIds), [
    allCropzoneIds
  ]);
  const cropzoneToFarmIdMap = useMemo(() => {
    const mapping = new Map();
    fft.forEach(farm => {
      (farm.children || []).forEach(field => {
        (field.children || []).forEach(cropzone => {
          mapping.set(cropzone.value, farm.value);
        });
      });
    });
    return mapping;
  }, [fft]);

  const globalSelectAllState =
    allCropzoneIds.length > 0 &&
    allCropzoneIds.length === cropZonesChecked.length;

  const handleCheck = useCallback(
    (checkedIds = [], targetNode = null) => {
      const nextCheckedIds = checkedIds.filter(id => allCropzoneIdSet.has(id));
      if (isEqual(nextCheckedIds, cropZonesChecked)) {
        return;
      }
      setCropZonesChecked(nextCheckedIds);
      const farmIds = Array.from(
        new Set(
          nextCheckedIds
            .map(cropzoneId => cropzoneToFarmIdMap.get(cropzoneId))
            .filter(Boolean)
        )
      );
      onChange(nextCheckedIds, farmIds, targetNode);
    },
    [
      allCropzoneIdSet,
      cropZonesChecked,
      cropzoneToFarmIdMap,
      onChange,
      setCropZonesChecked
    ]
  );

  const onFilterChange = e => {
    setFilterText(e.target.value);
  };

  const filterNodes = useCallback(
    (filtered, node) => {
      const children = (node.children || []).reduce(filterNodes, []);

      if (
        // Node's label matches the search string
        node.label
          .toLocaleLowerCase()
          .indexOf(filterText?.toLocaleLowerCase()) > -1 ||
        // Or a children has a matching node
        children.length
      ) {
        filtered.push({ ...node, children });
      }
      return filtered;
    },
    [filterText]
  );

  useEffect(() => {
    if (!filterText) {
      setNodesFiltered(fft);

      return;
    }
    const nodesFilter = prevState => prevState.reduce(filterNodes, []);

    setNodesFiltered(nodesFilter);
  }, [filterText, filterNodes, fft]);

  const handleSelectAllProperty = () => {
    if (globalSelectAllState) {
      handleCheck([]);
    } else if (onSelectAll) {
      onSelectAll(allCropzoneIds);
    } else {
      handleCheck(allCropzoneIds);
    }
  };

  return (
    <div
      className="filter-container"
      data-testid="cropseason-group-by-property"
    >
      <div className="mt-2 select-all-checkbox-wrapper">
        <Checkbox
          data-testid="select-all-properties-checkbox"
          onChange={() => handleSelectAllProperty()}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSelectAllProperty();
            }
          }}
          checked={globalSelectAllState}
          tabIndex={0}
        >
          Select All
        </Checkbox>
      </div>
      {enableFilter && (
        <div className="mb-2">
          <Input
            className=" filter-text"
            placeholder="Search..."
            value={filterText}
            onChange={onFilterChange}
          />
        </div>
      )}
      <CheckboxTree
        nodes={nodesFiltered}
        checked={checked}
        showNodeIcon={false}
        icons={{
          halfCheck: <IndeterminateCheckBox fontSize="small" />,
          uncheck: (
            <CheckBoxOutlineBlank
              fontSize="small"
              className="text-neutral-600"
            />
          ),
          check: <CheckBox fontSize="small" />,
          expandClose: (
            <ArrowRight fontSize="small" className="text-neutral-600" />
          ),
          expandOpen: (
            <ArrowDropDown fontSize="small" className="text-neutral-600" />
          )
        }}
        expanded={expanded}
        onCheck={(id, targetNode) => handleCheck(id, targetNode)}
        onExpand={ex => setExpanded(ex)}
        showNodeTitle
        optimisticToggle={false}
        disabled={disabled}
      />
    </div>
  );
};

GroupByFilterProperty.propTypes = {
  onChange: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  enableFilter: PropTypes.bool,
  disabled: PropTypes.bool,
  cropZonesChecked: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  setCropZonesChecked: PropTypes.func,
  onSelectAll: PropTypes.func
};

export default GroupByFilterProperty;
